import type { FieldPath, PathValue } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import type {
  EmailListingFragment,
  EmailPropertyFragment,
  EmailTemplateFormListingFragment,
  EmailUserFragment,
} from '@graphql/__generated__/graphql';
import type { EmailEditorSnippetData } from '@modules/_shared/components/email-editor';
import type { LotMandateType } from '@modules/_shared/types/lot';
import {
  createDocumentUrl,
  createTrackingMarkerUrl,
  getGlobalTemplateId,
} from '@modules/_shared/utils/document-url';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { hasOwnProp } from '@modules/_shared/utils/objects';
import { type Language, type Translate } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';
import { webURL } from '@shared/utils/web-url';

export const EMAIL_USER_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailUser on users {
    id
    first_name
    last_name
    gender
    title
    emails(order_by: [{ primary: desc }, { created_at: asc }], limit: 1) {
      id
      email
      primary
    }
    phone_numbers(order_by: { primary: desc }, limit: 1) {
      id
      number
    }
    organisation {
      id
      name
      ...OrganisationAddress
    }
  }
`);

export const EMAIL_PROPERTY_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailProperty on properties {
    id
    route
    locality
    municipality_place {
      id
      label
    }
    postcode
    street_number
    state
    property_type {
      id
      label
    }
    number_of_rooms
    living_surface
    land_surface
    latest_appraisal {
      id
      min
      max
      value
    }
  }
`);

export const EMAIL_LISTING_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailListing on lots {
    title
    sale_price
    rent_net
    rent_extra
  }
`);

type SuggestionField<
  T extends Record<string, any> = Record<string, any>,
  TPath extends FieldPath<T> | 'root' = FieldPath<T> | 'root',
> = TPath extends 'root'
  ? {
      key: string;
      fieldPath: TPath;
      getValue?: (value: T, t: Translate) => string | null;
    }
  : {
      key: string;
      fieldPath: TPath;
      getValue?: (
        value: PathValue<T, Exclude<TPath, 'root'>>,
        t: Translate,
      ) => string | null;
    };

type Suggestions = {
  id: string;
  name: string;
};

export type EmailReportsData = {
  lead_id: string | null;
  lot_id: string | null;
  cma_id: string | null;
  lead_template_id: string | null;
  lot_template_id: string | null;
  cma_template_id: string | null;
};

export type InitialEmailRecipient = {
  userId: string;
  buyerLeadId?: string;
};

export type EmailPropertyData = {
  municipality?: string | null;
  postcode?: string | null;
  route?: string | null;
  streetNumber?: string | null;
  locality?: string | null;
  state?: string | null;
  type?: string | null;
  leadId: string;
  lotId: string;
  leadTemplateId: string;
  lotTemplateId: string;
  cmaId: string;
  cmaTemplateId: string;
  mainType?: string;
  numberOfRooms?: number | null;
  livingSurface?: number | null;
  landSurface?: number | null;
};

export type EmailLotData = {
  id: string;
  rentNet: number | null;
  rentExtra: number | null;
  salePrice: number | null;
  product: { id: string } | null;
  primaryPropertyImage: { image: { url: string } } | null;
  title: string | null;
  property: {
    id: string;
    postcode: string | null;
    locality: string | null;
    propertyType: { label: string | null } | null;
    numberOfRooms: number | null;
    livingSurface: number | null;
    formattedAddress: string | null;
  };
  brochureTemplate: { id: string } | null;
  broker: null;
  seller: null;
  commissionRate: null;
  fixedFee: null;
  mandateType: LotMandateType;
  lotReference: string | null;
  offers: [];
};

export type EmailAgentData = {
  firstName: string | null;
  lastName: string | null;
  gender: string | null;
  primaryEmail: string | null;
  primaryPhoneNumber: string | null;
  organisationName: string | null;
  organisationAddress: string | null;
  title: string | null;
};

const userFields: SuggestionField<EmailUserFragment>[] = [
  { key: 'firstName', fieldPath: 'first_name' },
  { key: 'lastName', fieldPath: 'last_name' },
  {
    key: 'title',
    fieldPath: 'root',
    getValue: (user, t) =>
      getTitleLabel(user.title ?? null, t) ??
      getGenderLabel(user.gender ?? null, t),
  },
  { key: 'primaryPhoneNumber', fieldPath: 'phone_numbers.0.number' },
  { key: 'primaryEmail', fieldPath: 'emails.0.email' },
  { key: 'organisationName', fieldPath: 'organisation.name' },
  {
    key: 'organisationAddress',
    fieldPath: 'organisation',
    getValue: organisation =>
      organisation != null ? formatAddress(organisation, ', ', false) : null,
  },
];

const getGenderLabel = (gender: string | null, t: Translate) => {
  switch (gender) {
    case 'female':
      return t('madame');
    case 'male':
      return t('sir');
    default:
      return null;
  }
};

const getTitleLabel = (title: string | null, t: Translate) => {
  switch (title) {
    case 'sir':
      return t('sir');
    case 'madam':
      return t('madam');
    case 'professor':
      return t('professor');
    case 'doctor':
      return t('doctor');
    default:
      return null;
  }
};

const extractValue = (
  obj:
    | EmailUserFragment
    | EmailPropertyFragment
    | EmailListingFragment
    | EmailReportsData
    | null,
  field:
    | SuggestionField<EmailUserFragment>
    | SuggestionField<EmailPropertyFragment>
    | SuggestionField<EmailListingFragment>
    | SuggestionField<EmailReportsData>,
  t: Translate,
): string => {
  if (obj == null) {
    return '';
  }

  const value =
    field.fieldPath === 'root'
      ? obj
      : field.fieldPath.split('.').reduce((acc: any, key) => {
          if (acc == null) {
            return null;
          }

          if (typeof acc !== 'object') {
            return acc;
          }

          if (!hasOwnProp(acc, key)) {
            return null;
          }

          return acc[key];
        }, obj);

  if (field.getValue) {
    // Cast the value to any to handle the type mismatch because of `field` type safety.
    return (field.getValue as any)(value, t) ?? '';
  }

  // TODO: Maybe handle locale formatting here.
  return value?.toString() ?? '';
};

export const getEmailSenderSuggestions = (
  sender: EmailUserFragment | null,
  t: Translate,
): Suggestions[] => {
  return userFields.map(field => ({
    id: `#sender.${field.key}`,
    name: extractValue(sender, field, t),
  }));
};

export const toLegacyLot = (
  listing: EmailTemplateFormListingFragment,
  brochureTemplateId: string | null,
): EmailLotData => ({
  id: toGlobalId('Lot', listing.id),
  rentNet: listing.rent_net ?? null,
  rentExtra: listing.rent_extra ?? null,
  salePrice: listing.sale_price ?? null,
  broker: null,
  seller: null,
  commissionRate: null,
  fixedFee: null,
  mandateType: (listing.mandate_type ?? 'fixed_fee') as LotMandateType,
  lotReference: listing.lot_reference ?? null,
  offers: [],
  product: {
    id: toGlobalId('Product', listing.id),
  },
  primaryPropertyImage: {
    image: {
      url: listing.property.property_images[0]?.image.url ?? '',
    },
  },
  title: listing.title ?? null,
  property: {
    id: toGlobalId('Property', listing.property.id),
    postcode: listing.property.postcode ?? null,
    locality: listing.property.locality ?? null,
    propertyType: {
      label: listing.property.property_type?.label ?? null,
    },
    numberOfRooms: listing.property.number_of_rooms ?? null,
    livingSurface: listing.property.living_surface ?? null,
    formattedAddress: formatAddress(listing.property, ', '),
  },
  brochureTemplate:
    brochureTemplateId != null
      ? { id: getGlobalTemplateId(brochureTemplateId) }
      : null,
});

export const toLegacyProperty = (
  listing: EmailTemplateFormListingFragment,
  templateIds: {
    brochure: string | null;
    appraisalReport: string | null;
  },
): EmailPropertyData => {
  return {
    postcode: listing.property.postcode ?? null,
    municipality: listing.property.municipality_place?.label ?? null,
    route: listing.property.route ?? null,
    streetNumber: listing.property.street_number ?? null,
    locality: listing.property.locality ?? null,
    type: listing.property.property_type?.label ?? null,
    leadId: listing.lead?.id != null ? toGlobalId('Lead', listing.lead.id) : '',
    lotId: toGlobalId('Lot', listing.id),
    leadTemplateId:
      templateIds.appraisalReport != null
        ? getGlobalTemplateId(templateIds.appraisalReport)
        : '',
    lotTemplateId:
      templateIds.brochure != null
        ? getGlobalTemplateId(templateIds.brochure)
        : '',
    cmaId:
      listing.lead?.cma_reports[0]?.id != null
        ? toGlobalId('CmaReport', listing.lead?.cma_reports[0]?.id)
        : '',
    cmaTemplateId:
      listing.lead?.cma_reports[0]?.doc_template_id != null
        ? getGlobalTemplateId(listing.lead?.cma_reports[0]?.doc_template_id)
        : '',
    numberOfRooms: listing.property.number_of_rooms ?? null,
    livingSurface: listing.property.living_surface ?? null,
  };
};

export const getLotSnippetEntityData = (
  lot: EmailLotData | null,
  tenantId: string,
  language: Language,
): EmailEditorSnippetData => {
  const listingUrl = webURL('product', {
    id: lot?.product?.id ?? '#snippet.productId',
    tenantId,
    language,
    utm_medium: 'email',
    utm_source: 'crm_email',
    utm_campaign: 'lot_link',
  });

  if (lot != null) {
    const lotData: EmailEditorSnippetData = {
      type: 'lot',
      title: lot.title ?? '',
      postcode: lot.property.postcode ?? '',
      locality: lot.property.locality ?? '',
      listingUrl,
      thumbnailUrl: lot.primaryPropertyImage?.image.url,
      propertyTypeLabel: lot.property.propertyType?.label,
      numberOfRooms: lot.property.numberOfRooms?.toString() ?? '',
      livingSurface: lot.property.livingSurface?.toString() ?? '',
    };

    if (lot.brochureTemplate != null) {
      lotData.brochureUrl = createTrackingMarkerUrl(
        createDocumentUrl(lot.brochureTemplate.id, lot.id, language).toString(),
        Doc_Template_Type_Enum_Enum.LotBrochure,
      );
    }

    return lotData;
  }

  return {
    type: 'lot',
    title: '#snippet.title',
    postcode: '#snippet.postcode',
    locality: '#snippet.locality',
    listingUrl: decodeURIComponent(listingUrl),
    brochureUrl: '#snippet.brochureUrl',
    thumbnailUrl: '#snippet.thumbnailUrl',
    propertyTypeLabel: '#snippet.propertyTypeLabel',
    numberOfRooms: '#snippet.numberOfRooms',
    livingSurface: '#snippet.livingSurface',
  };
};

export const getListingLotSnippetEntityData = (
  listing: EmailTemplateFormListingFragment,
  defaultBrochureTemplateId: string | null,
  language: Language,
): EmailEditorSnippetData => {
  return getLotSnippetEntityData(
    toLegacyLot(
      listing,
      listing.brochure_doc_template_id ?? defaultBrochureTemplateId,
    ),
    listing.tenant_id,
    language,
  );
};

export type EmailMlsLotData = {
  id: string;
  title?: string | null;
  property?: {
    postcode?: string | null;
    locality?: string | null;
    number_of_rooms?: number | null;
    living_surface?: number | null;
    property_type?: { label?: string | null } | null;
    property_images?: ReadonlyArray<{ image: { url?: string | null } }> | null;
  } | null;
};

// An MLS lot belongs to another tenant: the snippet links only to the sender's
// brochure, branded to the sender via `overrideUserId` (the docs service checks
// the sender's team has MLS access to the lot), never to the owner's listing
// page. The link is not wrapped in a tracking marker: the email service only
// tracks documents of the sender's own tenant and would strip it at send time.
export const getMlsLotSnippetEntityData = (
  lot: EmailMlsLotData,
  brochureTemplateId: string,
  // null when the sender owns the lot: the brochure is already theirs, and the
  // docs service rejects an override for a user with no MLS share row.
  overrideUserId: string | null,
  language: Language,
): EmailEditorSnippetData => {
  const documentUrl = createDocumentUrl(
    getGlobalTemplateId(brochureTemplateId),
    toGlobalId('Lot', lot.id),
    language,
  );
  if (overrideUserId != null) {
    documentUrl.searchParams.set(
      'overrideUserId',
      toGlobalId('User', overrideUserId),
    );
  }
  const brochureUrl = documentUrl.toString();

  return {
    type: 'lot',
    title: lot.title ?? '',
    postcode: lot.property?.postcode ?? '',
    locality: lot.property?.locality ?? '',
    listingUrl: brochureUrl,
    brochureUrl,
    thumbnailUrl: lot.property?.property_images?.[0]?.image.url ?? undefined,
    propertyTypeLabel: lot.property?.property_type?.label ?? undefined,
    numberOfRooms: lot.property?.number_of_rooms?.toString() ?? '',
    livingSurface: lot.property?.living_surface?.toString() ?? '',
  };
};

export type EmailDevelopmentData = {
  slug: string | null;
  title: string | null;
  imageUrl: string | null;
  // Display fields are taken from one lot; the rooms/surface range spans the
  // lots fetched by the caller (the query caps how many are fetched).
  postcode: string | null;
  locality: string | null;
  propertyTypeLabel: string | null;
  lots: Array<{
    numberOfRooms: number | null;
    livingSurface: number | null;
  }>;
};

// Collapse to [min, max], or a single value when they're equal.
const toRange = (values: number[]): number[] => {
  if (values.length === 0) {
    return [];
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? [min] : [min, max];
};

export const getDevelopmentSnippetEntityData = (
  development: EmailDevelopmentData,
  tenantId: string,
  language: string,
): EmailEditorSnippetData => {
  const listingUrl = webURL('development', {
    slug: development.slug ?? '',
    tenantId,
    language,
    utm_medium: 'email',
    utm_source: 'crm_email',
    utm_campaign: 'development_link',
  });

  const numberOfRooms = development.lots
    .map(lot => lot.numberOfRooms)
    .filter((n): n is number => n != null);
  const livingSurface = development.lots
    .map(lot => lot.livingSurface)
    .filter((n): n is number => n != null);

  return {
    type: 'development',
    title: development.title ?? '',
    listingUrl,
    thumbnailUrl: development.imageUrl ?? undefined,
    postcode: development.postcode ?? '',
    locality: development.locality ?? '',
    propertyTypeLabel: development.propertyTypeLabel ?? undefined,
    numberOfRooms: toRange(numberOfRooms),
    livingSurface: toRange(livingSurface),
  };
};
